import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Eye, ShoppingCart } from 'lucide-react';

export default function DocsEvents() {
  const viewEventSchema = `{
  "brand_id": "brand_xyz",
  "product": {
    "product_id": "prod_123",        // required - your internal product ID
    "sku": "SKU-123-RED-M",          // optional
    "title": "Red Performance Legging",  // optional
    "url": "https://brand.com/products/red-performance-legging" // optional
  },
  "context": {
    "timestamp": "2025-12-22T15:30:00Z",
    "session_id": "sess_9a8b7c6d",   // auto-generated by ci.js
    "country": "US",
    "locale": "en-US",
    "user_agent": "Mozilla/5.0 ...",  // optional
    "referrer": "https://www.google.com/"  // optional
  }
}`;

  const purchaseEventSchema = `{
  "brand_id": "brand_xyz",
  "order": {
    "order_id": "order_789",         // required - your internal order ID
    "currency": "USD",               // required
    "total_value": 129.97,           // optional
    "items": [                       // required - at least one item
      {
        "product_id": "prod_123",    // required
        "sku": "SKU-123-RED-M",      // optional
        "title": "Red Performance Legging",
        "quantity": 2,
        "unit_price": 39.99
      },
      {
        "product_id": "prod_456",
        "sku": "SKU-456-BLACK",
        "title": "Black Sports Bra",
        "quantity": 1,
        "unit_price": 49.99
      }
    ]
  },
  "context": {
    "timestamp": "2025-12-22T15:31:30Z",
    "session_id": "sess_9a8b7c6d",
    "country": "US",
    "locale": "en-US"
  }
}`;

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30 mb-4">
          Integration
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Events
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Understand the event models that power Commerce Index Proof.
        </p>
      </div>

      {/* Privacy Notice */}
      <Alert className="bg-[#3EF1C7]/10 border-[#3EF1C7]/30 mb-8">
        <Info className="w-4 h-4 text-[#3EF1C7]" />
        <AlertDescription className="text-[#E2E8F0]">
          <strong>Privacy First:</strong> We do not accept PII (no emails, names, addresses). 
          All events are tied to brand_id and your product IDs only.
        </AlertDescription>
      </Alert>

      {/* Product View Event */}
      <section className="mb-12">
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Eye className="w-6 h-6 text-[#3EF1C7]" />
              <CardTitle className="text-white text-xl">Product View Event</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[#94A3B8] mb-4">
              Sent when a user views a product detail page (PDP). This event powers 
              the "X people viewed this" counter.
            </p>
            
            <h4 className="text-white font-medium mb-2">When to Send</h4>
            <ul className="list-disc list-inside text-[#94A3B8] mb-6 space-y-1">
              <li>When a visitor loads a product detail page</li>
              <li>After the main product information is visible</li>
              <li>Once per page load (avoid duplicate sends on scroll/interaction)</li>
            </ul>

            <h4 className="text-white font-medium mb-2">Data Model</h4>
            <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
              <code>{viewEventSchema}</code>
            </pre>

            <h4 className="text-white font-medium mt-6 mb-2">Field Reference</h4>
            <div className="space-y-2">
              {[
                { field: 'product.product_id', required: true, desc: 'Your internal product identifier (must be consistent)' },
                { field: 'product.sku', required: false, desc: 'Product SKU or variant identifier' },
                { field: 'product.title', required: false, desc: 'Product title for debugging' },
                { field: 'product.url', required: false, desc: 'Full URL of the product page' },
                { field: 'context.session_id', required: false, desc: 'Auto-generated by ci.js for grouping' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-[#22304A] last:border-0">
                  <code className="text-[#3EF1C7] text-sm bg-[#3EF1C7]/10 px-2 py-0.5 rounded flex-shrink-0">
                    {item.field}
                  </code>
                  {item.required && (
                    <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 text-xs">
                      Required
                    </Badge>
                  )}
                  <span className="text-[#94A3B8] text-sm">{item.desc}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Product Purchase Event */}
      <section className="mb-12">
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-6 h-6 text-[#7A5CFF]" />
              <CardTitle className="text-white text-xl">Product Purchase Event</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[#94A3B8] mb-4">
              Sent when an order is completed. This event powers the "Y purchases today" counter.
            </p>
            
            <h4 className="text-white font-medium mb-2">When to Send</h4>
            <ul className="list-disc list-inside text-[#94A3B8] mb-6 space-y-1">
              <li>On the order confirmation / "thank you" page</li>
              <li>After payment is confirmed</li>
              <li>Include all line items from the order</li>
            </ul>

            <h4 className="text-white font-medium mb-2">Data Model</h4>
            <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
              <code>{purchaseEventSchema}</code>
            </pre>

            <h4 className="text-white font-medium mt-6 mb-2">Field Reference</h4>
            <div className="space-y-2">
              {[
                { field: 'order.order_id', required: true, desc: 'Your internal order identifier' },
                { field: 'order.currency', required: true, desc: 'ISO 4217 currency code (e.g., USD, EUR)' },
                { field: 'order.items', required: true, desc: 'Array of purchased products' },
                { field: 'items[].product_id', required: true, desc: 'Must match product_id from view events' },
                { field: 'items[].quantity', required: false, desc: 'Number of units purchased (default: 1)' },
                { field: 'items[].unit_price', required: false, desc: 'Price per unit for analytics' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-[#22304A] last:border-0">
                  <code className="text-[#7A5CFF] text-sm bg-[#7A5CFF]/10 px-2 py-0.5 rounded flex-shrink-0">
                    {item.field}
                  </code>
                  {item.required && (
                    <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 text-xs">
                      Required
                    </Badge>
                  )}
                  <span className="text-[#94A3B8] text-sm">{item.desc}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Best Practices */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Best Practices
        </h2>
        <div className="space-y-3">
          {[
            {
              title: 'Use consistent product IDs',
              desc: 'The product_id in view events must match purchase events to link activity correctly.'
            },
            {
              title: 'Send events once per action',
              desc: 'Avoid sending duplicate events on page interactions like scrolling or tab changes.'
            },
            {
              title: 'Include timestamps',
              desc: 'While optional, timestamps help with debugging and accurate time window calculations.'
            },
            {
              title: 'Don\'t block rendering',
              desc: 'The ci.js script loads asynchronously. Events are queued until the script is ready.'
            }
          ].map((item, i) => (
            <div key={i} className="p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
              <h4 className="text-white font-medium mb-1">{item.title}</h4>
              <p className="text-sm text-[#94A3B8]">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
